import React, { useCallback, useState } from 'react';
import { useDispatch, useMappedState } from 'redux-react-hook';
import _reduce from 'lodash/reduce';
import _toNumber from 'lodash/toNumber';
import _isNaN from 'lodash/isNaN';
import _includes from 'lodash/includes';
import _toLower from 'lodash/toLower';
import { VALIDATION_MESSAGES } from 'common/validationMessages';
import BMI from 'styles/blocks/bodyMassIndex/index';
import { Error } from 'styles/blocks/error';
import {
  convertWeightImperialToMetric,
  convertWeightMetricToImperial,
  convertHeightImperialToMetric,
  convertHeightMetricToImperial,
  calculateTotalPounds,
  calculateTotalInches,
} from 'common/conversions';
import {
  updateBMIWeight,
  updateBMIHeight,
  toggleBMIWeightMeasure,
  toggleBMIHeightMeasure,
} from 'redux/actions/consultation/answer';
import { Field } from './field';
import { NextButton } from '../nextButton';

const bmi = {
  weight: {
    stone: { label: 'Stone (st)', maxLength: 2, isImperial: true },
    pounds: { label: 'Pounds (lb)', maxLength: 2, isImperial: true },
    kilograms: { label: 'Kilograms (kg)', maxLength: 3, isImperial: false },
  },
  height: {
    feet: { label: 'Feet (ft)', maxLength: 1, isImperial: true },
    inches: { label: 'Inches (in)', maxLength: 2, isImperial: true },
    centimetres: { label: 'Centimetres (cm)', maxLength: 3, isImperial: false },
  },
};

const isEquallyPopulatedOrEmpty = (value, valueTwo) => {
  if (_isNaN(_toNumber(value)) || _isNaN(_toNumber(valueTwo))) {
    return false;
  }
  return (value && valueTwo) || (!valueTwo && !value);
};

const getFieldDetails = (bmiConstants, bmiField, isImperial) => {
  return _reduce(bmiConstants, (result, value, key) => {
    if (value.isImperial !== isImperial) {
      return result;
    }
    return { ...result, [key]: { ...value, value: bmiField[key] } };
  }, {});
};

export function BodyMassIndex({ questionId, onClickNextQuestion }) {
  const mapState = useCallback(state => ({
    weight: state.consultationReducer.bmi.weight,
    isWeightImperial: state.consultationReducer.bmi.isWeightImperial,
    height: state.consultationReducer.bmi.height,
    isHeightImperial: state.consultationReducer.bmi.isHeightImperial,
    questionText: state.consultationReducer.question.text,
  }), []);
  const {
    isWeightImperial, isHeightImperial, height, weight, questionText,
  } = useMappedState(mapState);
  const dispatch = useDispatch();
  const [isPaedConsult] = useState(() => _includes(_toLower(questionText), 'child'));
  const [weightLabel] = useState(() => (isPaedConsult ? 'Tell us your child\'s weight (optional)' : 'Tell us your weight (optional)'));
  const [heightLabel] = useState(() => (isPaedConsult ? 'Tell us your child\'s height (optional)' : 'Tell us your height (optional)'));
  const [POINT_OF_VIEW] = useState(() => (isPaedConsult ? 'THIRD_PERSON' : 'FIRST_PERSON'));
  const [isInvalid, setIsInvalid] = useState({ weight: false, height: false });
  const [isMissingValue, setIsMissingValue] = useState({ weight: false, height: false });
  const [isInvalidValue, setIsInvalidValue] = useState({ weight: false, height: false });

  const handleClickNextQuestion = () => {
    setIsInvalid({ weight: false, height: false });
    setIsMissingValue({ weight: false, height: false });
    setIsInvalidValue({ weight: false, height: false });
    const isHeightMissing = isHeightImperial && !isEquallyPopulatedOrEmpty(height.feet, height.inches);
    const isWeightMissing = isWeightImperial && !isEquallyPopulatedOrEmpty(weight.stone, weight.pounds);
    if (isWeightMissing || isHeightMissing) {
      return setIsMissingValue({ weight: isWeightMissing, height: isHeightMissing });
    }
    const isInchesInvalidValue = isHeightImperial && (height.inches > 11 || height.inches < 0);
    const isPoundsInvalidValue = isWeightImperial && (weight.pounds > 13 || weight.pounds < 0);
    if (isPoundsInvalidValue || isInchesInvalidValue) {
      return setIsInvalidValue({ weight: isPoundsInvalidValue, height: isInchesInvalidValue });
    }
    const convertedHeight = isHeightImperial ? calculateTotalInches(height.feet, height.inches) : _toNumber(height.centimetres);
    const convertedWeight = isWeightImperial ? calculateTotalPounds(weight.stone, weight.pounds) : _toNumber(weight.kilograms);
    const isHeightProvided = height.feet > 0 || height.inches || height.centimetres;
    const isWeightProvided = weight.stone > 0 || weight.pounds || weight.kilograms;
    const isHeightAValidNumber = !_isNaN(_toNumber(height.feet)) && !_isNaN(_toNumber(height.inches)) && !_isNaN(_toNumber(height.centimetres));
    const isWeightAValidNumber = !_isNaN(_toNumber(weight.stone)) && !_isNaN(_toNumber(weight.pounds)) && !_isNaN(_toNumber(weight.kilograms));

    const isHeightOutsideValidRange = heightValue => {
      // isHeightImperial ? check inches are between 0ft 2in and 7ft 10in: check centimetres are in range
      return isHeightImperial ? (heightValue < 2 || heightValue > 94) : (heightValue < 5 || heightValue > 239);
    };
    const isWeightOutsideValidRange = weightValue => {
      // isWeightImperial ? check pounds are in range 0st 4lbs and 50st 1lbs : check kilograms are in range
      return isWeightImperial ? (weightValue < 4 || weightValue > 701) : (weightValue < 2 || weightValue > 318);
    };
    const isHeightInvalid = isHeightProvided && (!isHeightAValidNumber || isHeightOutsideValidRange(convertedHeight));
    const isWeightInvalid = isWeightProvided && (!isWeightAValidNumber || isWeightOutsideValidRange(convertedWeight));
    return isWeightInvalid || isHeightInvalid ? setIsInvalid({ weight: isWeightInvalid, height: isHeightInvalid }) : onClickNextQuestion();
  };

  const changeWeightMeasure = () => {
    if (isWeightImperial) {
      const kilograms = convertWeightImperialToMetric(weight.stone, weight.pounds);
      return dispatch(toggleBMIWeightMeasure({ kilograms }));
    }
    const { stone, pounds } = convertWeightMetricToImperial(weight.kilograms);
    return dispatch(toggleBMIWeightMeasure({ stone, pounds }));
  };

  const changeHeightMeasure = () => {
    if (isHeightImperial) {
      const centimetres = convertHeightImperialToMetric(height.feet, height.inches);
      return dispatch(toggleBMIHeightMeasure({ centimetres }));
    }
    const { feet, inches } = convertHeightMetricToImperial(height.centimetres);
    return dispatch(toggleBMIHeightMeasure({ feet, inches }));
  };

  return (
    <BMI>
      {isInvalid.weight && <Error>{VALIDATION_MESSAGES.BMI.WEIGHT.OUT_OF_RANGE[isWeightImperial ? 'IMPERIAL' : 'METRIC']}</Error>}
      {isMissingValue.weight && <Error>{VALIDATION_MESSAGES.BMI.WEIGHT.MISSING_VALUE.IMPERIAL[POINT_OF_VIEW]}</Error>}
      {isInvalidValue.weight && <Error>{VALIDATION_MESSAGES.BMI.WEIGHT.INVALID_VALUE.IMPERIAL[POINT_OF_VIEW]}</Error>}
      <Field
        values={getFieldDetails(bmi.weight, weight, isWeightImperial)}
        questionText={weightLabel}
        buttonText={`Switch to ${isWeightImperial ? 'kilograms (kg)' : 'stone (st) and pounds (lb)'}`}
        onClickConvert={changeWeightMeasure}
        onChange={weightAndMeasure => dispatch(updateBMIWeight(weightAndMeasure))}
      />
      {isInvalid.height && <Error>{VALIDATION_MESSAGES.BMI.HEIGHT.OUT_OF_RANGE[isHeightImperial ? 'IMPERIAL' : 'METRIC']}</Error>}
      {isMissingValue.height && <Error>{VALIDATION_MESSAGES.BMI.HEIGHT.MISSING_VALUE.IMPERIAL[POINT_OF_VIEW]}</Error>}
      {isInvalidValue.height && <Error>{VALIDATION_MESSAGES.BMI.HEIGHT.INVALID_VALUE.IMPERIAL[POINT_OF_VIEW]}</Error>}
      <Field
        values={getFieldDetails(bmi.height, height, isHeightImperial)}
        questionText={heightLabel}
        buttonText={`Switch to ${isHeightImperial ? 'centimetres (cm)' : 'feet (ft) and inches (in)'}`}
        onClickConvert={changeHeightMeasure}
        onChange={heightAndMeasure => dispatch(updateBMIHeight(heightAndMeasure))}
      />
      <NextButton dataAutomation={`nextButton-${questionId}`} onClick={handleClickNextQuestion} />
    </BMI>
  );
}
